import { FC, useCallback, useEffect } from 'react';
import { Form, Table } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchDynamicsData } from 'store/analytics/slice';
import dayjs from 'dayjs';
import { selectAnalyticsDynamics } from 'store/analytics/selectors';
import { shallowEqual } from 'react-redux';
import { formatNumberWithSpaces } from '@medicusunion/front-utils';
import { useOrganizationsStateContext } from 'providers/Organizations';

import { analyticsReviewCache } from 'utils/cacheWithQuery';
import { AnalyticsFiltersType } from '../../_types';
import Filters from '../Filters';
import { money } from 'utils/price';
import { getMonthName } from '../../_utils';

interface IProps {
  utmSource?: string;
}

const Dynamics: FC<IProps> = ({ utmSource }) => {
  const dispatch = useAppDispatch();

  const { parentSelected } = useOrganizationsStateContext();

  const data = useAppSelector(
    selectAnalyticsDynamics(parentSelected),
    shallowEqual
  );

  const [form] = Form.useForm();
  useEffect(() => {
    analyticsReviewCache.init();
  }, []);

  const fetchData = useCallback(
    (values: AnalyticsFiltersType, utm_source?: string) => {
      const { dates, ...rest } = values;

      if (!dates) {
        return;
      }

      const [start, end] = dates;

      dispatch(
        fetchDynamicsData({
          startDate: dayjs(start).startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs(end).endOf('month').format('YYYY-MM-DD'),
          utm_source,
          ...rest
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const values = form.getFieldsValue([
      'dates',
      'utm_medium',
      'utm_campaign',
      'utm_term'
    ]);

    fetchData(values, utmSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmSource]);

  const onFilter = useCallback(
    (values: AnalyticsFiltersType) => {
      fetchData(values, utmSource);
    },
    [fetchData, utmSource]
  );

  return (
    <>
      <Form
        form={form}
        initialValues={{
          dates: [
            dayjs().add(-1, 'month').startOf('month'),
            dayjs().endOf('month')
          ]
        }}
        onFinish={onFilter}
      >
        <Filters dateMode="month" onReset={() => form.submit()} />
      </Form>
      <Table
        dataSource={data}
        rowKey="month"
        columns={[
          {
            key: 'month',
            dataIndex: 'month',
            title: 'Date',
            fixed: 'left',
            render(value) {
              return getMonthName(value);
            }
          },
          {
            key: 'totalUsers',
            dataIndex: 'totalUsers',
            title: 'Users',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'transactions',
            dataIndex: 'transactions',
            title: 'Total Orders',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'conversionRate',
            dataIndex: 'conversionRate',
            title: 'Conversion Rate',
            render(value) {
              return `${formatNumberWithSpaces(+(value * 100).toFixed(2))}%`;
            }
          },
          {
            key: 'revenue',
            dataIndex: 'revenue',
            title: 'Revenue Share',
            render(value) {
              return money(+value, { currency: 'USD' });
            }
          },
          {
            key: 'subscription_monthly_quantity',
            dataIndex: 'subscription_monthly_quantity',
            title: 'Monthly Subscriptions (Qty)',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'subscription_monthly_price',
            dataIndex: 'subscription_monthly_price',
            title: 'Monthly Subscriptions (Price)',
            render(value) {
              return money(+value, { currency: 'USD' });
            }
          },
          {
            key: 'subscription_yearly_quantity',
            dataIndex: 'subscription_yearly_quantity',
            title: 'Yearly Subscriptions (Qty)',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'subscription_yearly_price',
            dataIndex: 'subscription_yearly_price',
            title: 'Yearly Subscriptions (Price)',
            render(value) {
              return money(+value, { currency: 'USD' });
            }
          },
          {
            key: 'video_consultation_quantity',
            dataIndex: 'video_consultation_quantity',
            title: 'Video Consultations (Qty)',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'video_consultation_price',
            dataIndex: 'video_consultation_price',
            title: 'Video Consultations (Price)',
            render(value) {
              return money(+value, { currency: 'USD' });
            }
          },
          {
            key: 'radiology_quantity',
            dataIndex: 'radiology_quantity',
            title: 'Radiology (Qty)',
            render(value) {
              return formatNumberWithSpaces(+value);
            }
          },
          {
            key: 'radiology_price',
            dataIndex: 'radiology_price',
            title: 'Radiology (Price)',
            render(value) {
              return money(+value, { currency: 'USD' });
            }
          }
        ]}
        scroll={{ x: 'max-content' }}
        pagination={false}
      />
    </>
  );
};

export default Dynamics;
